import Client from "./_client";

/**
 * Onboard a Case Master free-trial user.
 *
 * This is a direct call from the frontend: the backend creates the user and
 * emails them their login credentials (the password is never returned in the
 * response).
 *
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.email
 * @param {string} params.contact contact number; required by the endpoint's
 *   validation schema as a top-level field
 * @param {('free'|'pro'|'premium')} [params.plan='free'] accepted but ignored —
 *   this endpoint always provisions the free plan
 * @param {Object} [params.metadata] extra profile details stored on the user's
 *   metadata: { whatsappNumber, collegeName, academicYear, aspiringRole, referralSource }
 * @returns {Promise<Object>} the `data` payload from a successful response
 * @throws {Error & { status?: number }} on failure. `status === 409` means an
 *   account already exists for this email; any other status is a genuine
 *   validation/server failure.
 */
export const subscribeCaseMasterUser = async ({ name, email, contact, plan = "free", metadata }) => {
  const result = await new Client({
    path: `/user/b2c/free-trial`,
    payload: { name, email, contact, plan, metadata },
  }).post();

  if (!(result.ok || result.code === 200)) {
    const error = new Error(
      result.message || "Uh Oh! Something went wrong, please try again."
    );
    error.status = result.status;
    // An already-registered email comes back as a 400 ValidationError rather
    // than a 409, so normalise it here and let callers route those users to
    // login instead of showing a generic failure.
    if (/already registered/i.test(result.message || "")) error.status = 409;
    throw error;
  }

  return result?.data;
};
